import React, { useState, useEffect, useContext, useRef } from 'react';
import PlusFlair from './PlusFlair';
import { NavLink } from 'react-router-dom';

import ReactPlayer from 'react-player';

import bannerLogo from '../images/Jook_Web_White.png';

import Ticker from './Ticker';
import IdleScreen from './IdleScreen';
import LeaderTrophy from './LeaderTrophy';
import JoinQrCode, { jukeboxUrl } from './JoinQrCode';
import SongProgress, { SongProgressHandle } from './SongProgress';
import TriviaScreen from './TriviaScreen';
import ScaleLoader from 'react-spinners/ScaleLoader';

import * as JKYModel from '../custom-types';

import defaultValues from '../config/defaultValues.json';

import usePlayerUpdateHandler from '../utils/custom-hooks/PlayerUpdateHandler';
import useLeaderboard from '../utils/custom-hooks/LeaderboardHandler';
import { mixShoutouts } from '../utils/Shoutouts';
import ShoutoutTickerItem from './ShoutoutTickerItem';
import ShoutoutPopup from './ShoutoutPopup';
import AdminMessageBanner from './AdminMessageBanner';

import { diff } from '../utils/Helpers';
import { readSavedPlayback, savePlayback } from '../utils/PlaybackResume';

import { authContext } from '../contexts/AuthContext';

import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';

import './PlayerView.scss';

interface PlayerViewProps {
  playerIdentifier: string;
}

const PlayerView = (props: PlayerViewProps) => {
  const [duration, setDuration] = useState(0);
  const [needsNextVideo, setNeedsNextVideo] = useState(false);
  const [connectedPlayer, setConnectedPlayer] = useState<JKYModel.Player>();
  const [activeVideo, setActiveVideo] = useState<JKYModel.QueueVideo>();
  const progressRef = useRef<SongProgressHandle>(null);
  const context = useContext(authContext);
  const currentUser = context.currentUser;
  console.log('currentUser is ', currentUser);

  console.log('props');
  console.log(props);
  const selectedPlayerAuth = context.player;
  const selectedPlayerName = selectedPlayerAuth.name;
  const urlPlayerIdentifier = props.playerIdentifier;
  if (selectedPlayerAuth.playerIdentifier !== urlPlayerIdentifier) {
    console.log(`${selectedPlayerAuth.playerIdentifier} does not match ${urlPlayerIdentifier}`);
  }
  const { player, error, trivia, shoutouts, popup, adminMessage } = usePlayerUpdateHandler(urlPlayerIdentifier, 'tv');
  const leaders = useLeaderboard(player);
  let loading = !player;

  // Trivia night. The queue keeps playing under the questions, so patrons who are not
  // playing can still add songs and hear them. The owner's chosen music only fills the gaps
  // when the queue is empty. If the song changes screen on the way in or out, the screen
  // remembers how far into it it was and carries on from there.
  // The same goes for a reload (a site admin's, or onto a new version after an update):
  // the screen starts out knowing where it was, saved by utils/PlaybackResume.
  const triviaOn = !!(trivia && trivia.active);
  const [musicIndex, setMusicIndex] = useState(0);
  const musicFailures = useRef(0);
  const [savedPlayback] = useState(() => readSavedPlayback(urlPlayerIdentifier));
  const resumePoint = useRef<{ videoId: number; seconds: number }>(savedPlayback);
  useEffect(() => {
    setMusicIndex(0);
    musicFailures.current = 0;
  }, [triviaOn]);

  // The song that finished and is waiting for the server to move on from it. If the server
  // is restarting for an update just then, the request fails, and nothing used to ask again:
  // the TV sat on the finished song until somebody reloaded it. Now it keeps asking until the
  // server is back, as long as that song is still the one on the screen.
  const finishedVideoId = useRef<number>(null);
  const REMOVE_RETRY_MS = 3000;
  const REMOVE_RETRY_LIMIT = 100;

  const removeVideo = async (playerId: number, videoId: number, attempt: number = 0) => {
    const token = context.auth.token;
    const params = { playerId, videoId };
    if (attempt === 0) {
      finishedVideoId.current = videoId;
    }
    api
      .players('removeVideo', 'POST', token, params)
      .then(response => {
        if (connectedPlayer && connectedPlayer.nowPlayingVideo) {
          // let nextVideo = queueVideos.shift();
          setActiveVideo(connectedPlayer.nowPlayingVideo);
        }
      })
      .catch(error => {
        console.log('There has been a problem with your search operation: ' + error.message);
        // No answer, or the server (or Cloudflare in front of it) failing: it is down or
        // restarting. A 4xx is a real answer, such as the song already being gone.
        const serverDown = !error.status || error.status >= 500;
        if (serverDown && attempt < REMOVE_RETRY_LIMIT && finishedVideoId.current === videoId) {
          window.setTimeout(() => {
            if (finishedVideoId.current === videoId) {
              removeVideo(playerId, videoId, attempt + 1);
            }
          }, REMOVE_RETRY_MS);
        }
      });
  };

  // `reason` is 'error' when YouTube would not play it and 'skip' when the owner skipped it.
  // Anything else, such as the player's own end event, is a song that played through.
  const onEndedVideo = async (reason?: unknown) => {
    console.log('onEnded');
    if (!connectedPlayer || !connectedPlayer.nowPlayingVideo) {
      return;
    }

    let playerId = connectedPlayer.id;
    let videoId = connectedPlayer.nowPlayingVideo.id;
    // Counted by the TV, so a song counts once per play wherever it was added from.
    if (typeof reason !== 'string' && connectedPlayer.nowPlayingVideo.thirdPartyId !== SkyNewsYoutubeID) {
      trackEvent('song_played', {
        player_code: connectedPlayer.playerIdentifier,
        video_id: connectedPlayer.nowPlayingVideo.thirdPartyId,
      });
    }

    if (videoId !== null) {
      await removeVideo(playerId, videoId);
    }
  };

  // A song YouTube will not play (private, removed, blocked in this country) never ends,
  // so the screen would sit on the error for good. Move on as if it had finished. Not the
  // filler, though: with an empty queue the server just puts the filler straight back, and
  // the screen would go round that loop forever.
  const onVideoError = (e: any) => {
    console.log('onError', e);
    if (activeVideo && activeVideo.thirdPartyId !== SkyNewsYoutubeID) {
      trackEvent('song_unplayable', { player_code: urlPlayerIdentifier, video_id: activeVideo.thirdPartyId });
      onEndedVideo('error');
    }
  };

  const checkSkyNewsSkip = () => {
    if (
      connectedPlayer &&
      connectedPlayer.nowPlayingVideo &&
      connectedPlayer.queue &&
      connectedPlayer.queue.videos &&
      connectedPlayer.queue.videos.length > 0
    ) {
      let video = connectedPlayer.nowPlayingVideo;
      if (video.thirdPartyId === SkyNewsYoutubeID) {
        //console.log('SKIP SKY NEWS');
        onEndedVideo();
      }
    }
  };

  const checkVideoSkip = () => {
    if (connectedPlayer && connectedPlayer.skipVideo) {
      console.log('checkVideoSkip');
      const token = context.auth.token;
      const params = { skipVideo: false };
      api
        .players(`${connectedPlayer.id}`, 'PATCH', token, params)
        .then(response => {
          console.log('SKIP CURRENTLY PLAYING VIDEO');
          onEndedVideo('skip');
        })
        .catch(error => {
          console.log('There has been a problem with your search operation: ' + error.message);
        });
    }
  };

  // A new song on the screen means the server has moved on, so stop asking it to.
  const activeVideoId = activeVideo ? activeVideo.id : null;
  useEffect(() => {
    if (finishedVideoId.current !== null && finishedVideoId.current !== activeVideoId) {
      finishedVideoId.current = null;
    }
  }, [activeVideoId]);

  useEffect(() => {
    checkVideoSkip();
    checkSkyNewsSkip();
  });

  // Closed captions, switched from the control panel. cc_load_policy only counts when a
  // video loads, and the embed builds the first video's frame before it has read this
  // config, so the YouTube player's captions module is also switched directly: that takes
  // effect on the song already playing, and is applied again as each song starts.
  const showCaptions = !player || player.showCaptions !== false;
  const videoElement = useRef<any>(null);
  const applyCaptions = () => {
    const youtube = videoElement.current && videoElement.current.api;
    try {
      if (youtube && showCaptions) {
        youtube.loadModule('captions');
      } else if (youtube) {
        youtube.setOption('captions', 'track', {});
        youtube.unloadModule('captions');
      }
    } catch (e) {
      console.log('Could not switch captions', e);
    }
  };
  useEffect(applyCaptions, [showCaptions]);

  if (loading) {
    let loadingTitle = 'Launching Your Player Experience';
    if (selectedPlayerName) {
      loadingTitle = `Launching ${selectedPlayerName}`;
    }
    return (
      <div className="loading-wrapper">
        <h1 style={{ marginBottom: 20 }}>{loadingTitle}</h1>
        <ScaleLoader
          cssOverride={{ display: 'flex' }}
          height={35}
          width={4}
          radius={2}
          color={'#fff'}
          loading={loading}
        />
      </div>
    );
  }

  let hasDifferences = true;
  if (connectedPlayer) {
    const lastValues = connectedPlayer;
    const newValues = player;
    const difference = diff(newValues, lastValues);
    if (difference.length === 0) {
      hasDifferences = false;
    }
  }

  if (hasDifferences) {
    setConnectedPlayer(player);
    if (connectedPlayer && connectedPlayer.nowPlayingVideo) {
      // let nextVideo = queueVideos.shift();
      setActiveVideo(connectedPlayer.nowPlayingVideo);
    }
  }

  const onPlay = () => {
    console.log('onPlay');
  };

  const onPause = () => {
    console.log('onPause');
  };

  // react-player 3 hands through the media element's own events, so playback
  // position now arrives as timeupdate on the element rather than as the
  // library's own progress object.
  const onTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    progressRef.current?.update(e.currentTarget.currentTime, e.currentTarget.duration);
    if (activeVideo) {
      resumePoint.current = { videoId: activeVideo.id, seconds: e.currentTarget.currentTime };
      savePlayback(urlPlayerIdentifier, activeVideo.id, e.currentTarget.currentTime);
    }
  };

  // Back from trivia: pick the song up where it stopped. Only once, and only for the same
  // song; a song that changed meanwhile starts from the top as usual.
  const onJukeboxPlaying = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    applyCaptions();
    const resume = resumePoint.current;
    if (resume && activeVideo && resume.videoId === activeVideo.id && resume.seconds > 3) {
      const element = e.currentTarget;
      if (element.currentTime < resume.seconds - 2) {
        try {
          element.currentTime = resume.seconds;
        } catch (err) {
          console.log('Could not resume the song', err);
        }
      }
    }
    resumePoint.current = null;
  };

  const musicIds = (triviaOn && trivia.music && trivia.music.videoIds) || [];
  const musicId = musicIds.length > 0 ? musicIds[musicIndex % musicIds.length] : null;
  const nextMusic = () => setMusicIndex(index => index + 1);
  // A song YouTube refuses is skipped, but a list where every song fails stops trying
  // rather than spinning through it forever.
  const onMusicError = () => {
    musicFailures.current += 1;
    if (musicFailures.current < musicIds.length) {
      nextMusic();
    }
  };

  // let ref = reactPlayer: ReactPlayer => {
  //   this.reactPlayer: ReactPlayer = reactPlayer;
  // };

  let isPlayerPlaying = true;
  let playbackRate = 1.0;
  let playerVolumeLevel: number = 1.0;
  let isPlayerMuted = false;
  let isPlayerOwner = true;
  let videoCount = 0;
  let isHiddenAdArea = false;
  let isHiddenTickerArea = false;
  let tickerLineOne = 'Welcome to Jookable, the Ultimate Video Jukebox.';
  let tickerLineTwo = 'Use your smartphone and connect to this player, then add your favorite videos to play.';
  let tickerLineThree = 'If you have ideas let us know.';
  let queueVideos: JKYModel.QueueVideo[] = [];
  let url = '';
  let activeUsername = 'admin';
  let SkyNewsYoutubeID = '9Auq9mYxFEE';
  let hideLeftButton = !isPlayerOwner;

  let activeVideoUsernameValue;

  if (connectedPlayer) {
    isPlayerPlaying = connectedPlayer.isPlaying;
    playerVolumeLevel = connectedPlayer.volumeLevel;
    isPlayerMuted = connectedPlayer.isMuted;
    isHiddenAdArea = connectedPlayer.isHiddenAdArea;
    isHiddenTickerArea = connectedPlayer.isHiddenTickerArea;

    if (connectedPlayer.user) {
      isPlayerOwner = connectedPlayer.user.id === currentUser.id;
      activeUsername = connectedPlayer.user.username;
      console.log('activeUsername is ', activeUsername);
      console.log('isPlayerOwner ', isPlayerOwner);
    }

    if (connectedPlayer.tickerLineOne) {
      tickerLineOne = connectedPlayer.tickerLineOne;
    }
    if (connectedPlayer.tickerLineTwo) {
      tickerLineTwo = connectedPlayer.tickerLineTwo;
    }
    if (connectedPlayer.tickerLineThree) {
      tickerLineThree = connectedPlayer.tickerLineThree;
    }

    if (connectedPlayer.queue && connectedPlayer.queue.videos && connectedPlayer.queue.videos.length > 0) {
      queueVideos = connectedPlayer.queue.videos;
    }
    videoCount = queueVideos.length;

    if (connectedPlayer.nowPlayingVideo) {
      let nextVideo = connectedPlayer.nowPlayingVideo;
      if (!activeVideo || nextVideo.id !== activeVideo.id) {
        setActiveVideo(nextVideo);
      }
    } else {
      // Only when there is something to take. With nothing playing and nothing queued this
      // set undefined over undefined on every render, which React stops as an endless
      // loop, and the TV went blank instead of showing the idle screen.
      if (!activeVideo && queueVideos.length > 0) {
        let nextVideo = queueVideos.shift();
        setActiveVideo(nextVideo);
      }
    }
  }

  let isSuperBump = false;
  let isBump = false;
  if (!!activeVideo) {
    // Built from the YouTube id rather than taken from the song's own link. The server used
    // to store whatever link the phone sent, so an old queue row can still point at any file
    // on the internet; the screen only ever plays YouTube.
    url = /^[A-Za-z0-9_-]{11}$/.test(activeVideo.thirdPartyId || '')
      ? `https://www.youtube.com/watch?v=${activeVideo.thirdPartyId}`
      : '';
    isSuperBump = activeVideo.isSuperBump;
    isBump = activeVideo.isBump;

    if (activeVideo.owner) {
      if (activeVideo.owner.username) {
        activeVideoUsernameValue = activeVideo.owner.username;
      } else {
        activeVideoUsernameValue = 'admin';
      }
    }

    if (activeVideo.thirdPartyId === SkyNewsYoutubeID && videoCount === 1) {
      videoCount = 0;
    }
  }

  // The server still queues the Sky News video when the queue runs dry, and
  // that is what makes a new song take over. The screen no longer plays it:
  // the stream went private, and venues would rather show their own logo.
  const isIdle = !activeVideo || activeVideo.thirdPartyId === SkyNewsYoutubeID;

  // Paid shout-outs ride in the ticker, spread through its own lines. The owner can hide the
  // ticker; a paid shout-out still shows, in a ticker of its own, until its time is up.
  const tickerItems: React.ReactNode[] = isHiddenTickerArea
    ? []
    : [
        tickerLineOne && (
          <span className="playerticker-item" key="line-1">
            {tickerLineOne}
          </span>
        ),
        tickerLineTwo && (
          <span className="playerticker-item" key="line-2">
            {tickerLineTwo}
          </span>
        ),
        tickerLineThree && (
          <span className="playerticker-item" key="line-3">
            {tickerLineThree}
          </span>
        ),
        leaders.length > 0 && (
          <span className="playerticker-item playerticker-leaders" key="leaders">
            Top 5 this week:
            {leaders.map((leader, index) => (
              <span className="playerticker-leader" key={leader.id}>
                <LeaderTrophy index={index} className="playerticker-trophy" />
                {leader.username} ({leader.plays})
              </span>
            ))}
          </span>
        ),
      ].filter(Boolean);
  const shoutoutItems = shoutouts.map(shoutout => <ShoutoutTickerItem key={`shoutout-${shoutout.id}`} shoutout={shoutout} />);
  const showTicker = !isHiddenTickerArea || shoutoutItems.length > 0;

  let marquee = (
    <Ticker rate={60}>
      <div className="playerticker-container">{mixShoutouts(tickerItems, shoutoutItems)}</div>
    </Ticker>
  );

  // Gold for a super bump, silver for a bump, periwinkle for everything else.
  // The glow around now playing takes this colour, so the room can see a paid song.
  const tier = isSuperBump ? 'super' : isBump ? 'bump' : 'regular';

  // While a song plays, the bottom panels slim down to one row so the video has
  // as much of the screen as it can.
  const liftVideo = !isIdle;

  const upNext = queueVideos
    .filter(video => video.thirdPartyId !== SkyNewsYoutubeID && (!activeVideo || video.id !== activeVideo.id))
    .slice(0, 3);

  const wrapperClass = [
    'player-wrapper',
    'player-glass',
    `player-tier-${tier}`,
    liftVideo ? 'player-lifted' : '',
    showTicker ? '' : 'player-no-ticker',
    popup ? 'player-shoutout-popup' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={triviaOn ? `${wrapperClass} player-trivia` : wrapperClass}>
      {triviaOn && <div className="trivia-backdrop" />}

      <header className="player-header">
        <NavLink className="player-home-link" to="/home" key="home-link">
          <img className="player-logo" src={bannerLogo} alt="Jookable" />
        </NavLink>

        {connectedPlayer && (
          <div className="player-id">
            <small>ID</small>
            <b>{connectedPlayer.playerIdentifier.toUpperCase()}</b>
          </div>
        )}

        <div className="player-join">
          <div className="player-join-text">
            {triviaOn ? (
              <>
                Play trivia
                <br />
                on your phone
              </>
            ) : (
              <>
                Add your
                <br />
                favorite video
              </>
            )}
          </div>
        </div>
      </header>

      {/* A Jookable site admin's message, across the screen for the seconds they chose. A row
          of its own: the video gives up the height, nothing is drawn over it. */}
      {adminMessage && <AdminMessageBanner key={`admin-message-${adminMessage.id}`} message={adminMessage} />}

      {/* YouTube's API policies forbid drawing anything over the embedded player, so the
          video has a row of its own and the panels sit around it. */}
      <div className="player-middle">
        {triviaOn && (
          <TriviaScreen
            trivia={trivia}
            venueName={connectedPlayer ? connectedPlayer.name : ''}
            songTitle={!isIdle && activeVideo ? activeVideo.title : null}
          />
        )}
        <div className="playervideo">
          {triviaOn && isIdle ? (
            musicId ? (
              <ReactPlayer
                key={`music-${musicIndex}`}
                className="react-player embed-responsive-item"
                width="100%"
                height="100%"
                src={`https://www.youtube.com/watch?v=${musicId}`}
                playing={isPlayerPlaying}
                volume={playerVolumeLevel}
                muted={isPlayerMuted}
                controls={false}
                onPlaying={() => (musicFailures.current = 0)}
                onEnded={nextMusic}
                onError={onMusicError}
              />
            ) : null
          ) : isIdle ? (
            <IdleScreen
              player={connectedPlayer}
              playerIdentifier={urlPlayerIdentifier}
              canEdit={!!connectedPlayer && isPlayerOwner}
            />
          ) : (
            <ReactPlayer
              ref={videoElement}
              config={{ youtube: { cc_load_policy: showCaptions ? 1 : 0 } }}
              className="react-player embed-responsive-item"
              width="100%"
              height="100%"
              src={url}
              playing={isPlayerPlaying}
              playbackRate={playbackRate}
              volume={playerVolumeLevel}
              muted={isPlayerMuted}
              controls={isPlayerOwner}
              onReady={() => console.log('onReady')}
              onStart={() => console.log('onStart')}
              onPlay={onPlay}
              onPlaying={onJukeboxPlaying}
              onPause={onPause}
              onWaiting={() => console.log('onWaiting')}
              onSeeked={e => console.log('onSeeked', e)}
              onEnded={onEndedVideo}
              onError={onVideoError}
              onTimeUpdate={onTimeUpdate}
              onDurationChange={e => setDuration(e.currentTarget.duration)}
            />
          )}
        </div>

        {/* During trivia a new paid shout-out takes the QR code's place under the video for a
            few seconds. Under it, never over it. */}
        {triviaOn && popup && <ShoutoutPopup key={`popup-${popup.shoutout.id}`} shoutout={popup.shoutout} compact />}

        {/* Beside the video, never over it. Scanning it opens this player's jukebox page.
            The song leaderboard, when the owner has it on, sits under it. */}
        {connectedPlayer && !(triviaOn && popup) && (
          <div className="player-side">
            <aside className="player-qr">
              <JoinQrCode className="player-qr-code" url={jukeboxUrl(window.location.origin, connectedPlayer.playerIdentifier)} />
              <div className="player-qr-caption">{triviaOn ? 'Scan to play' : 'Scan to add songs'}</div>
            </aside>

            {leaders.length > 0 && !triviaOn && (
              <section className="player-leaders">
                <div className="player-label">Top 5 this week</div>
                {leaders.map((leader, index) => (
                  <div className="player-leader-row" key={leader.id}>
                    <LeaderTrophy index={index} className="player-leader-rank" />
                    <PlayerAvatar owner={leader} className="player-avatar" />
                    <span className="player-leader-name">{leader.username.toUpperCase()}</span>
                    <span className="player-leader-plays">{leader.plays}</span>
                  </div>
                ))}
              </section>
            )}
          </div>
        )}
      </div>

      <div className="player-bottom">
        {/* A new paid shout-out pops up here, below the video, for a few seconds before it
            drops into the ticker. Now playing and up next step aside meanwhile, and the
            video's row gives up the height it needs. Nothing is drawn over the video. */}
        {!triviaOn && popup && <ShoutoutPopup key={`popup-${popup.shoutout.id}`} shoutout={popup.shoutout} />}

        <div className="player-cards" hidden={!triviaOn && !!popup}>
          {!!activeVideo && !isIdle && (
            <section className="player-card player-now">
              <div className="player-label">Now playing</div>
              <div className="player-now-main">
                <PlayerAvatar owner={activeVideo.owner} className="player-avatar player-avatar-now" />
                <div className="player-now-text">
                  <div className="player-now-title">{activeVideo.title}</div>
                  <div className="player-now-meta">
                    {!!activeVideoUsernameValue && <span>{activeVideoUsernameValue.toUpperCase()}</span>}
                    {isSuperBump && <span className="player-tier-chip">Super bump</span>}
                    {!isSuperBump && isBump && <span className="player-tier-chip">Bump</span>}
                  </div>
                </div>
              </div>
              <SongProgress key={activeVideo.id} ref={progressRef} visible={true} />
            </section>
          )}

          {upNext.length > 0 && (
            <section className="player-card player-next">
              <div className="player-label">
                Up next &middot; <b>{videoCount}</b> in queue
              </div>
              {upNext.map(video => (
                <div className="player-next-row" key={video.id}>
                  <PlayerAvatar
                    owner={video.owner}
                    className={`player-avatar${
                      video.isSuperBump ? ' player-avatar-super' : video.isBump ? ' player-avatar-bump' : ''
                    }`}
                  />
                  <div className="player-next-text">
                    <div className="player-next-title">{video.title}</div>
                    <div className="player-next-user">
                      {video.owner && video.owner.username ? video.owner.username.toUpperCase() : 'ADMIN'}
                    </div>
                  </div>
                  {video.duration && <span className="player-next-duration">{video.duration}</span>}
                </div>
              ))}
            </section>
          )}
        </div>

        {showTicker && <div className="player-ticker">{marquee}</div>}
      </div>
    </div>
  );
};

interface PlayerAvatarProps {
  owner?: { username?: string; profilePicUrl?: string | null; isPlus?: boolean };
  className: string;
}

// The patron's picture where they have one, and their initials where they don't.
// A Jookable+ member's gets the ring and "+" (PlusFlair).
const PlayerAvatar = ({ owner, className }: PlayerAvatarProps) => {
  const isPlus = !!(owner && owner.isPlus);
  if (owner && owner.profilePicUrl) {
    return (
      <PlusFlair isPlus={isPlus}>
        <img className={className} src={owner.profilePicUrl} alt="" />
      </PlusFlair>
    );
  }
  const name = (owner && owner.username) || 'admin';
  const initials = name.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase();
  return (
    <PlusFlair isPlus={isPlus}>
      <span className={className} aria-hidden="true">
        {initials}
      </span>
    </PlusFlair>
  );
};

export default PlayerView;
